/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { ChangeDetectorRef, Component } from '@angular/core';
import { DateTimeApiService } from '../../../shared/http';
import { DateTimeModel } from '../../../shared/models';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { LbErrorService } from '../../../shared/services/components';
import { LicenceService } from '../../../shared/services/utilities';
import {
    LoadingService,
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    UtilsService,
    ValidationService
} from '../../../../../core/services';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-date-time',
    templateUrl: './dateTime.component.html',
    styleUrls: ['./dateTime.component.scss']
})

export class DateTimeComponent extends BaseComponent {

    /**
     * Timezone form.
     */
    public timeZoneForm: FormGroup;

    /**
     * Ntp form.
     */
    public ntpForm: FormGroup;

    /**
     * Datetime form.
     */
    public dateTimeForm: FormGroup;

    /**
     * Stores dateTime data.
     */
    public dateTime: DateTimeModel = {
        dateTime: '',
        timeZone: '',
        ntpEnabled: false,
        ntp: []
    };

    /**
     * Store the page date and time.
     */
    public pageDateTime: DateTimeModel = {
        dateTime: '',
        timeZone: '',
        ntpEnabled: false,
        ntp: []
    };

    /**
     * Ntp enabled toggle form element (ntp / date & time)
     */
    public ntpEnabled: any;

    /**
     * Variable to store warning modal content, depending on the type of warning.
     */
    public warningModalContent: any = {};

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Store the difference between two dates in days.
     */
    private daysDifference: number = 0;

    /**
     * Store the difference between two times in hours.
     */
    private hoursDifference: number = 0;

    /**
     * To store the initial data for the NTP form.
     * @private
     */
    private initialNTPData: any = {};

    /**
     * To store the initial data for the date form.
     * @private
     */
    private initialDateData: any = {};

    /**
     * To store the initial data for the date and time form.
     * @private
     */
    private initialTimeZoneData: any = {};

    constructor(
        private cdr: ChangeDetectorRef,
        private dateTimeApiService: DateTimeApiService,
        private formBuilder: FormBuilder,
        private lbErrorService: LbErrorService,
        private licenseService: LicenceService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private utilsService: UtilsService
    ) {
        super();

        this.setupFormGroups();
        this.subscribeToFormChanges();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Get the ntp form values.
     */
    get ntpFields(): FormArray {
        return this.ntpForm.get('ntp') as FormArray;
    }

    /**
     * Get the number of days before the license expires.
     */
    get daysToExpire(): number {
        return this.licenseService.licenceObj.daysToExpire;
    }

    /**
     * Set the timezone form values.
     * @param $event
     */
    set timeZoneFormValue($event: any) {
        this.dateTime.timeZone = $event;
        this.timeZoneForm.get('timeZone').setValue($event);
        this.cdr.detectChanges();
    }

    /**
     * Returns form control as FormControl.
     * @param control
     */
    public getFormControl(control: any): FormControl {
        return control as FormControl;
    }

    /**
     * Toggle state between ntp / date & time manual inputs
     * @param state
     */
    public toggleNtp(state: boolean): any {
        this.ntpEnabled = state;
        this.ntpForm.get('ntpEnabled').setValue(state);
    }

    /**
     * Submit button state.
     */
    public submitButtonIsDisabled(): boolean {
        return (this.dateTimeForm.invalid || this.dateTimeForm.pristine) &&
            (this.ntpForm.invalid || this.ntpForm.pristine) &&
            (this.timeZoneForm.invalid || this.timeZoneForm.pristine);
    }

    /**
     * Submit helper.
     */
    public submitHelper(): Observable<any> {
        if (this.daysDifference > 0 || this.hoursDifference > 0) {
            this.showSubmitConfirmModal();
        } else {
            return this.submitForms();
        }
    }

    /**
     * Submit all forms that have been edited.
     */
    public submitForms(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const apiCalls = this.getApiCalls();

            const hasMultipleApiCalls: boolean = Object.keys(apiCalls)?.length > 1;
            if (hasMultipleApiCalls) {
                this.notifyService.muteLocalNotifications();
            }

            forkJoin(apiCalls)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (response: any): void => {
                        if (hasMultipleApiCalls) {
                            this.notifyService.unmuteLocalNotifications();
                            this.notifyService.success('Settings updated');
                        }
                        observer.next(response);
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                    },
                    complete: (): void => {
                        this.modalService.close('confirmation-box');
                        this.setUpPageData();
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Show date warning confirmation modal.
     */
    private showSubmitConfirmModal(): void {
        this.loadingService.loadingButtonEmitter.next(false);
        const licenseDaysLeft = this.daysToExpire - this.daysDifference;

        if ((this.daysDifference >= 1 && licenseDaysLeft > 0) ||
            (!this.daysDifference && this.hoursDifference >= 1)) {
            this.warningModalContent = {
                title: 'date_time_date_out_of_range_relogin_title',
                text: 'date_time_date_out_of_range_relogin_text'
            };
        }

        if (this.daysDifference >= 1 && licenseDaysLeft <= 0) {
            this.warningModalContent = {
                title: 'date_time_date_out_of_range_license_title',
                text: 'date_time_date_out_of_range_license_text'
            };
        }

        const confirmationObject = {
            title: this.warningModalContent.title,
            message: this.warningModalContent.text,
            callBack: this.submitForms.bind(this),
            options: { translateText: true }
        };
        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Return a list of valid api calls based on active/updated form components
     * @private
     */
    private getApiCalls(): any {
        const apiCalls = {};
        if (!this.ntpForm.pristine) {
            apiCalls['ntpForm'] = this.submitNtp();
        }
        if (!this.timeZoneForm.pristine) {
            apiCalls['timeZoneForm'] = this.submitZone();
        }
        if (!this.dateTimeForm.pristine) {
            apiCalls['dateTimeForm'] = this.submitDateTime();
        }

        return apiCalls;
    }

    /**
     * Post the ntp servers.
     * @private
     */
    private submitNtp(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const ntpFormValues = this.ntpForm.value;
            ntpFormValues.ntp = this.utilsService.cleanArray(ntpFormValues.ntp);
            this.dateTimeApiService.addNtp(ntpFormValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.ntpForm, error.error.errors);
                        this.logService.error('DateTimeComponent: submitNtp', 'Error submitting data', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Post date and time to server.
     * @private
     */
    private submitDateTime(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const date = this.dateTimeForm.value.date;
            const time = this.dateTimeForm.value.time;

            this.dateTimeApiService.addTimeDate(time, date)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.lbErrorService.pageWithErrors(this.dateTimeForm, error.error.errors);
                        this.logService.error('DateTimeComponent: submitDateTime', 'Error submitting data', error);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Post time zone to server.
     * @private
     */
    private submitZone(): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const timeZone = this.timeZoneForm.value.timeZone;
            this.dateTimeApiService.addTimeZone(timeZone)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.pageDateTime.timeZone = timeZone;
                        this.initialTimeZoneData = this.timeZoneForm.value;
                        this.timeZoneForm.markAsPristine();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.dateTimeApiService.getNodeDateTime()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (data: DateTimeModel): void => {
                    this.setClassVariables(data);
                    this.patchFormData(data);
                },
                error: (error: ErrorResponseModel): void => {
                    // We've lost the token! It's expired! force user to re-login!
                    this.modalService.openModalInParent('relogin');
                    this.logService.error('dateTime.component: setUpPageData',
                        'Unable to retrieve api dateTime:' + error);
                }
            });
    }

    /**
     * Set the class variables.
     * @param data
     * @private
     */
    private setClassVariables(data: any): void {
        this.dateTime = data;
        const { date, time } = this.utilsService.getDateTimeObjectFromIsoDateTime(data.dateTime);
        this.pageDateTime = this.utilsService.duplicateObject(this.dateTime);
        // Used only on the html page to produce a human-readable date/time stamp. Don't include time as it will
        // localise it to the client browser time; manually add the time to the string instead without localising.
        this.pageDateTime.dateTime = this.utilsService.createDateTimeString(this.pageDateTime.dateTime, false) + ' ' + time;
        this.initialTimeZoneData = { timeZone: data.timeZone };
        this.initialDateData = { date: date, time: time };
        this.initialNTPData = data.ntp;
    }

    /**
     * Patch form values.
     * @private
     */
    private patchFormData(data: any): void {
        const { date, time } = this.utilsService.getDateTimeObjectFromIsoDateTime(data.dateTime);
        this.patchDateTime(date, time);
        this.patchNtp(data);
    }

    /**
     * Patch the date & time form.
     * @param date
     * @param time
     * @private
     */
    private patchDateTime(date: string, time: string): void {
        this.dateTimeForm.patchValue({ date, time });
        this.dateTimeForm.markAsPristine();
    }

    /**
     * Patch values in the form NTP.
     * @param ntpData
     * @private
     */
    private patchNtp(ntpData: any): void {
        const missingNtps: number = this.ntpFields.length - ntpData.ntp.length;
        const emptyArray: any[] = new Array(missingNtps);
        ntpData.ntp.push(...emptyArray);
        this.ntpForm.patchValue(ntpData);
        this.initialNTPData = this.ntpForm.value;
        this.ntpEnabled = ntpData.ntpEnabled;
        this.ntpForm.markAsPristine();
    }

    /**
     * Set up the form group.
     * @private
     */
    private setupFormGroups(): void {
        this.timeZoneForm = this.formBuilder.group({
            timeZone: [null, Validators.required]
        });

        this.ntpForm = this.formBuilder.group({
            ntpEnabled: [null],
            ntp: new FormArray(
                [
                    new FormControl(null, [ValidationService.ipDomainValidator]),
                    new FormControl(null, [ValidationService.ipDomainValidator]),
                    new FormControl(null, [ValidationService.ipDomainValidator])
                ], { validators: ValidationService.atLeastOneRequired }
            )
        });

        this.dateTimeForm = this.formBuilder.group({
            date: [null, Validators.required],
            time: [null, Validators.required]
        });
    }

    /**
     * Subscribe to the form changes.
     * @private
     */
    private subscribeToFormChanges(): void {
        this.timeZoneForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: timeZoneForm => {
                    this.utilsService.debounce('timeZoneForm', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialTimeZoneData, timeZoneForm)) {
                            this.timeZoneForm.markAsPristine();
                        } else {
                            this.timeZoneForm.markAsDirty();
                        }
                    });
                }
            });

        this.ntpForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: ntpForm => {
                    this.utilsService.debounce('ntpForm', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialNTPData, ntpForm)) {
                            this.ntpForm.markAsPristine();
                        } else {
                            this.ntpForm.markAsDirty();
                        }
                    });
                }
            });

        this.dateTimeForm.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: dateTimeForm => {
                    this.utilsService.debounce('dateTimeForm', () => {
                        if (!this.utilsService.hasFormDataChanged(this.initialDateData.date, dateTimeForm['date'])
                            && !this.utilsService.hasFormDataChanged(this.initialDateData.time, dateTimeForm['time'])) {
                            this.dateTimeForm.markAsPristine();
                        } else {
                            this.daysDifference = this.calculateDaysDifference(this.initialDateData.date, dateTimeForm['date']);
                            if (!this.daysDifference) {
                                this.hoursDifference = this.calculateHoursDifference(this.initialDateData.time, dateTimeForm['time']);
                            }
                            this.dateTimeForm.markAsDirty();
                        }
                    });
                }
            });
    }

    /**
     * Helper function to return number of days between two dates.
     * @param initialDate
     * @param formDate
     * @private
     */
    private calculateDaysDifference(initialDate: string, formDate: string): number {
        const date1: Date = new Date(initialDate);
        const date2: Date = new Date(formDate);
        const dateDifference: number = date2.getTime() - date1.getTime();

        return dateDifference / (1000 * 3600 * 24);
    }

    /**
     * Helper function to return the number of hours between two times.
     * @param initialTime
     * @param formTime
     * @private
     */
    private calculateHoursDifference(initialTime: string, formTime: string): number {
        const timeStart: number = new Date('2023-01-01 ' + initialTime).getHours();
        const timeEnd: number = new Date('2023-01-01 ' + formTime).getHours();

        return timeEnd - timeStart;
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'update-config': [
                { path: '/system/datetime/timezone', methods: ['PUT'] },
                { path: '/system/datetime/ntp', methods: ['PUT'] },
                { path: '/system/datetime', methods: ['PUT'] },
            ],
            'timeZoneForm': [{ path: '/system/datetime/timezone', methods: ['PUT'] }],
            'ntpForm': [{ path: '/system/datetime/ntp', methods: ['PUT'] }],
            'dateTimeForm': [{ path: '/system/datetime', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup.timeZoneForm) {
            this.timeZoneForm.disable();
        }

        if (!this.permissionsLookup.ntpForm) {
            this.ntpForm.disable();
        }

        if (!this.permissionsLookup.dateTimeForm) {
            this.dateTimeForm.disable();
        }
    }
}
